import { tw } from "@careinfo/tailwind";
import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import APIS from "../../../constant/Endpoint";
import useDeleteHook from "../../../customHooks/useDeleteHook";
import useGetHook from "../../../customHooks/useGetHook";
import usePostHook from "../../../customHooks/usePostHook";
import Group from "../../../Icons/GropIcon";
import UserIcon from "../../../Icons/UserIcon";
import { NotificationContext } from "../context";
import { NotificationSkeleton } from "../Notification";
import Delete from "./Delete";
import Inbox from "./Inbox";
import LastMessage from "./LastMessage";

const Chat = () => {
  const { searchTerm } = useContext(NotificationContext);
  const queryClient = useQueryClient();
  const {
    data: participants,
    isLoading,
    isRefetching,
  } = useGetHook({
    url: APIS.GROUP,
    queryKey: ["group", `group-${searchTerm}`],
    params: {
      search: searchTerm,
    },
  });
  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: ["message"],
  });

  const { deleteData, isDeleting } = useDeleteHook({
    queryKey: ["group"],
  });

  const handleDeleteGroup = async (id: any) => {
    const url = `${APIS.GROUP}${id}/`;
    try {
      await deleteData({ url, setError: () => {} });
    } catch (err) {
      console.error(err);
    }
  };

  const handleClearMessage = async (id: any) => {
    const url = `${APIS.GROUP}${id}/clear-messages/`;
    try {
      const res = await mutateAsync({ url, formData: {}, setError: () => {} });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["group"],
          refetchType: "active",
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const { openInbox, setOpenInbox } = useContext(NotificationContext);

  if (isLoading) {
    return (
      <>
        {Array.from({ length: 10 }, () => (
          <NotificationSkeleton key={crypto.randomUUID()} />
        ))}
      </>
    );
  }

  return !openInbox.open ? (
    <div className={tw`overflow-scroll h-[100%]`}>
      {Array.isArray(participants) &&
        participants.map((item, index) => (
          <button
            key={item?.id}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenInbox({ open: true, group: item });
            }}
            className={tw`flex w-full items-center  border-b mb-auto py-2 pl-4 cursor-pointer focus:outline-none`}
          >
            <div className={tw`flex w-full items-center gap-x-4  `}>
              <div className={tw`w-12 h-12 relative`}>
                {item?.is_private ? (
                  <UserIcon width={40} height={40} />
                ) : (
                  <Group width={40} height={40} />
                )}
                <svg
                  className={tw`absolute -bottom-2 -right-2 w-10 h-10`}
                  viewBox="0 0 16 16"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="#39FF14"
                >
                  <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
                </svg>
              </div>
              <div className={tw`w-full text-left`}>
                <h4 className={tw`font-semibold  `}>{item?.name}</h4>
                <LastMessage message={item?.last_message?.message} />
              </div>
            </div>
            <div className={tw`flex gap-x-2 items-center`}>
              <span className={tw`text-blue-400 whitespace-nowrap `}>
                {item?.last_message?.created}
              </span>
              <Delete
                handleDeleteGroup={handleDeleteGroup}
                handleClearMessage={handleClearMessage}
                isDeleting={isDeleting || isPostLoading || isRefetching}
                id={item?.id}
              />
            </div>
          </button>
        ))}
    </div>
  ) : (
    <Inbox group={openInbox?.group} setOpenInbox={setOpenInbox} />
  );
};

export default Chat;

export function ChatSekeleton() {
  return (
    <div
      className={tw`flex items-center gap-x-4 animate-pulse p-2 bg-white border-b`}
    >
      <div className={tw`h-10 w-10 rounded-full bg-gray-300`}></div>
      <div className={tw`w-20 h-3 rounded-md bg-gray-200`}></div>
    </div>
  );
}
